import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import MuiButton from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import Logo from 'src/components/Logo';
import { track, Events } from 'src/utils/analytics';

const NAV_LINKS = [
  { label: 'Home', path: '/' },
  { label: 'Features', path: '/features' },
  { label: 'Pricing', path: '/pricing' },
  { label: 'Blog', path: '/blog' },
  { label: 'Contact', path: '/contact' },
];

const SHADOW = '3px 3px 0px #1e1e1e';

export default function LandingNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleNav = (link: typeof NAV_LINKS[0]) => {
    navigate(link.path);
    setDrawerOpen(false);
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          borderBottom: '2px solid #1e1e1e',
        }}
      >
        <Toolbar
          sx={{
            maxWidth: 1200,
            width: '100%',
            mx: 'auto',
            px: { xs: 2, md: 6 },
            py: 1,
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            onClick={() => navigate('/')}
          >
            <Logo variant="regular" height="32px" />
          </Box>

          {/* Desktop nav */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              gap: 1,
            }}
          >
            {NAV_LINKS.map((link) => (
              <MuiButton
                key={link.label}
                onClick={() => handleNav(link)}
                sx={{
                  color: '#2C2C2C',
                  fontFamily: 'Lato, Inter, sans-serif',
                  fontWeight: location.pathname === link.path ? 700 : 500,
                  fontSize: '0.9rem',
                  textTransform: 'none',
                  borderBottom: location.pathname === link.path ? '2px solid #2C2C2C' : '2px solid transparent',
                  borderRadius: 0,
                  '&:hover': { backgroundColor: 'transparent', borderBottom: '2px solid #2C2C2C' },
                }}
              >
                {link.label}
              </MuiButton>
            ))}

            <MuiButton
              onClick={() => navigate('/login')}
              sx={{
                color: '#2C2C2C',
                fontFamily: 'Montserrat, Inter, sans-serif',
                fontWeight: 700,
                fontSize: '0.875rem',
                textTransform: 'none',
                ml: 2,
                '&:hover': { backgroundColor: 'transparent' },
              }}
            >
              Log in
            </MuiButton>
            <MuiButton
              variant="contained"
              onClick={() => { track(Events.ClickedCTA, { location: 'nav', label: 'Sign Up Free', destination: '/register' }); navigate('/register'); }}
              sx={{
                backgroundColor: '#2C2C2C',
                color: '#FFFFFF',
                fontFamily: 'Montserrat, Inter, sans-serif',
                fontWeight: 700,
                fontSize: '0.875rem',
                textTransform: 'none',
                borderRadius: '6px',
                border: '2px solid #1e1e1e',
                boxShadow: SHADOW,
                px: 3,
                '&:hover': {
                  backgroundColor: '#1e1e1e',
                  boxShadow: '1px 1px 0px #1e1e1e',
                  transform: 'translate(2px, 2px)',
                },
                transition: 'all 100ms ease-in-out',
              }}
            >
              Sign Up Free
            </MuiButton>
          </Box>

          {/* Mobile hamburger */}
          <IconButton
            onClick={() => setDrawerOpen(true)}
            sx={{ display: { xs: 'flex', md: 'none' }, color: '#2C2C2C' }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Mobile drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 280, pt: 2 } }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2 }}>
          <IconButton onClick={() => setDrawerOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <List>
          {NAV_LINKS.map((link) => (
            <ListItemButton key={link.label} onClick={() => handleNav(link)}>
              <ListItemText
                primary={link.label}
                primaryTypographyProps={{
                  fontFamily: 'Lato, Inter, sans-serif',
                  fontWeight: location.pathname === link.path ? 700 : 400,
                  color: '#2C2C2C',
                }}
              />
            </ListItemButton>
          ))}
          <ListItemButton onClick={() => { navigate('/login'); setDrawerOpen(false); }}>
            <ListItemText primary="Log in" primaryTypographyProps={{ fontWeight: 600, color: '#2C2C2C' }} />
          </ListItemButton>
          <Box sx={{ px: 2, mt: 2 }}>
            <MuiButton
              fullWidth
              variant="contained"
              onClick={() => { navigate('/register'); setDrawerOpen(false); }}
              sx={{
                backgroundColor: '#2C2C2C',
                color: '#FFFFFF',
                fontFamily: 'Montserrat, Inter, sans-serif',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: '6px',
                border: '2px solid #1e1e1e',
                boxShadow: SHADOW,
                py: 1.5,
                '&:hover': { backgroundColor: '#1e1e1e' },
              }}
            >
              Sign Up Free
            </MuiButton>
          </Box>
        </List>
      </Drawer>
    </>
  );
}
